import { useState, useEffect } from "react";
import { 
  DollarSign, Package, Image, Loader2, Check, AlertTriangle,
  ShoppingCart, Download
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "./ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "./ui/card";
import { Alert, AlertDescription } from "./ui/alert";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const GalleryDownloadPricing = ({ galleryId, onUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    downloads_enabled: false,
    pricing_type: "per_photo",
    price_per_photo: 5.00,
    package_price: null,
    allow_individual: true,
    watermark_previews: true
  });

  useEffect(() => {
    fetchPricing();
  }, [galleryId]);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(
        `${API}/downloads/gallery/${galleryId}/pricing`,
        { withCredentials: true }
      );
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch pricing:", error);
    } finally {
      setLoading(false);
    }
  };

  const savePricing = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/downloads/gallery/${galleryId}/pricing`,
        settings,
        { withCredentials: true }
      );
      toast.success("Pricing settings saved");
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save pricing");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <Card data-testid="gallery-download-pricing">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShoppingCart className="w-5 h-5" />
          Sell Downloads
        </CardTitle>
        <CardDescription>
          Allow clients to purchase and download high-resolution photos
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enable Downloads */}
        <div className="flex items-center justify-between">
          <div>
            <Label>Enable Photo Sales</Label>
            <p className="text-sm text-muted-foreground">
              Clients can purchase photos from this gallery
            </p>
          </div>
          <Switch
            checked={settings.downloads_enabled}
            onCheckedChange={(checked) => setSettings({ ...settings, downloads_enabled: checked })}
          />
        </div>

        {settings.downloads_enabled && (
          <>
            {/* Pricing Type */}
            <div className="space-y-2">
              <Label>Pricing Model</Label>
              <Select
                value={settings.pricing_type}
                onValueChange={(value) => setSettings({ ...settings, pricing_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_photo">
                    <div className="flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      Per Photo
                    </div>
                  </SelectItem>
                  <SelectItem value="package">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Full Gallery Package
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Per Photo Price */}
            {(settings.pricing_type === "per_photo" || settings.allow_individual) && (
              <div className="space-y-2">
                <Label>Price Per Photo</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    min="1"
                    step="0.50"
                    value={settings.price_per_photo || ""}
                    onChange={(e) => setSettings({ ...settings, price_per_photo: parseFloat(e.target.value) || null })}
                    className="pl-9"
                    placeholder="5.00"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Minimum $1 per photo
                </p>
              </div>
            )}

            {/* Package Price */}
            {settings.pricing_type === "package" && (
              <>
                <div className="space-y-2">
                  <Label>Full Gallery Package Price</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      min="1"
                      step="5"
                      value={settings.package_price || ""}
                      onChange={(e) => setSettings({ ...settings, package_price: parseFloat(e.target.value) || null })}
                      className="pl-9"
                      placeholder="99.00"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Price for all photos in this gallery
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Allow Individual Purchases</Label>
                    <p className="text-sm text-muted-foreground">
                      Let clients buy single photos too
                    </p>
                  </div>
                  <Switch
                    checked={settings.allow_individual}
                    onCheckedChange={(checked) => setSettings({ ...settings, allow_individual: checked })}
                  />
                </div>
              </>
            )}

            {/* Watermark Setting */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Watermark Previews</Label>
                <p className="text-sm text-muted-foreground">
                  Show watermark on unpurchased photos
                </p>
              </div>
              <Switch
                checked={settings.watermark_previews}
                onCheckedChange={(checked) => setSettings({ ...settings, watermark_previews: checked })}
              />
            </div>

            {/* Revenue Info */}
            <Alert>
              <DollarSign className="w-4 h-4" />
              <AlertDescription>
                <strong>Your earnings:</strong> You keep 94% of each sale (Pro plan). 
                Platform fee: 6%.
              </AlertDescription>
            </Alert>
          </>
        )}

        {/* Save Button */}
        <Button onClick={savePricing} disabled={saving} className="w-full">
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Check className="w-4 h-4 mr-2" />
          )}
          Save Pricing Settings
        </Button>
      </CardContent>
    </Card>
  );
};

export default GalleryDownloadPricing;
